<template>
  <div class="contents">
    <GuestTopBar />

    <AppNavHeader
      variant="default"
      @open-mobile-menu="openMobileMenu"
    >
      <template #center>
        <div class="hidden lg:flex lg:gap-x-12">
          <UPopover
            :content="menuContent"
            :ui="productsUi"
          >
            <button
              type="button"
              class="dark:text-content-inverse flex font-semibold gap-x-1 items-center text-content-level-900 text-sm/6"
            >
              {{ $t('common.navigation.product')
              }}<ChevronDownIcon
                class="dark:text-content-level-500 flex-none size-5 text-content-level-400"
                aria-hidden="true"
              />
            </button>

            <template #content>
              <div class="p-4">
                <div
                  v-for="item in products"
                  :key="item.name"
                  class="dark:hover:bg-surface-canvas/5 flex gap-x-6 group hover:bg-surface-level-50 items-center p-4 relative rounded-lg text-sm/6"
                >
                  <div
                    class="bg-surface-level-50 dark:bg-surface-level-700/50 dark:group-hover:bg-surface-level-700 flex flex-none group-hover:bg-surface-canvas items-center justify-center rounded-lg size-11"
                  >
                    <component
                      :is="item.icon"
                      class="dark:group-hover:text-content-inverse dark:text-content-level-400 group-hover:text-primary size-6 text-content-level-600"
                      aria-hidden="true"
                    />
                  </div>

                  <div class="flex-auto">
                    <a
                      :href="item.href"
                      class="block dark:text-content-inverse font-semibold text-content-level-900"
                    >
                      {{ item.name }}
                      <span class="absolute inset-0" />
                    </a>

                    <p class="dark:text-content-level-400 mt-1 text-content-level-600">
                      {{ item.description }}
                    </p>
                  </div>
                </div>
              </div>

              <div
                class="bg-surface-level-50 dark:bg-surface-level-700/50 dark:divide-border-inverse/10 divide-border-level-900/5 divide-x grid grid-cols-2"
              >
                <a
                  v-for="item in callsToAction"
                  :key="item.name"
                  :href="item.href"
                  class="dark:hover:bg-surface-level-700/50 dark:text-content-inverse flex font-semibold gap-x-2.5 hover:bg-surface-level-100 items-center justify-center p-3 text-content-level-900 text-sm/6"
                >
                  <component
                    :is="item.icon"
                    class="dark:text-content-level-500 flex-none size-5 text-content-level-400"
                    aria-hidden="true"
                  />
                  {{ item.name }}
                </a>
              </div>
            </template>
          </UPopover>

          <a
            href="#"
            class="dark:text-content-inverse font-semibold text-content-level-900 text-sm/6"
          >
            {{ $t('common.navigation.features') }}
          </a>

          <a
            href="#"
            class="dark:text-content-inverse font-semibold text-content-level-900 text-sm/6"
          >
            {{ $t('common.navigation.marketplace') }}
          </a>

          <UPopover
            :content="menuContent"
            :ui="companyUi"
          >
            <button
              type="button"
              class="dark:text-content-inverse flex font-semibold gap-x-1 items-center text-content-level-900 text-sm/6"
            >
              {{ $t('common.navigation.company')
              }}<ChevronDownIcon
                class="dark:text-content-level-500 flex-none size-5 text-content-level-400"
                aria-hidden="true"
              />
            </button>

            <template #content>
              <div
                v-for="item in company"
                :key="item.name"
                class="dark:hover:bg-surface-canvas/5 hover:bg-surface-level-50 p-4 relative rounded-lg"
              >
                <a
                  :href="item.href"
                  class="block dark:text-content-inverse font-semibold text-content-level-900 text-sm/6"
                >
                  {{ item.name }}
                  <span class="absolute inset-0" />
                </a>

                <p class="dark:text-content-level-400 mt-1 text-content-level-600 text-sm/6">
                  {{ item.description }}
                </p>
              </div>
            </template>
          </UPopover>
        </div>
      </template>

      <template #preActions>
        <!-- Cart Trigger -->
        <DraftOrdersTrigger />
      </template>

      <template #bottom>
        <MobileNavDrawer
          :open="mobileMenuOpen"
          @close="closeMobileMenu"
        />

        <!-- Cart Drawer -->
        <DraftOrdersDrawer
          :is-open="cartStore.isDraftOrdersOpen"
          @close="cartStore.closeDraftOrdersCart"
        />
      </template>
    </AppNavHeader>
  </div>
</template>

<script setup lang="ts">
import UPopover from '@nuxt/ui/components/Popover.vue'
import DraftOrdersDrawer from '~/components/DraftOrdersDrawer.vue'
import DraftOrdersTrigger from '~/components/DraftOrdersTrigger.vue'
import GuestTopBar from '~/components/GuestTopBar.vue'
import AppNavHeader from '~/components/AppNavHeader.vue'
import MobileNavDrawer from '~/components/MobileNavDrawer.vue'
import { useCartStore } from '~/stores/useCartStore'

import { ChevronDownIcon, PhoneIcon, PlayCircleIcon } from '@heroicons/vue/20/solid'
import {
  ArrowPathIcon,
  ChartPieIcon,
  CursorArrowRaysIcon,
  FingerPrintIcon,
  SquaresPlusIcon
} from '@heroicons/vue/24/outline'

const cartStore = useCartStore()

const products = [
  {
    description: 'Get a better understanding of your traffic',
    href: '#',
    icon: ChartPieIcon,
    name: 'Analytics'
  },
  {
    description: 'Speak directly to your customers',
    href: '#',
    icon: CursorArrowRaysIcon,
    name: 'Engagement'
  },
  {
    description: "Your customers' data will be safe and secure",
    href: '#',
    icon: FingerPrintIcon,
    name: 'Security'
  },
  {
    description: 'Connect with third-party tools',
    href: '#',
    icon: SquaresPlusIcon,
    name: 'Integrations'
  },
  {
    description: 'Build strategic funnels that will convert',
    href: '#',
    icon: ArrowPathIcon,
    name: 'Automations'
  }
]

const callsToAction = [
  { href: '#', icon: PlayCircleIcon, name: 'Watch demo' },
  { href: '#', icon: PhoneIcon, name: 'Contact sales' }
]

const company = [
  {
    description: 'Learn more about our company values and mission to empower others',
    href: '#',
    name: 'About us'
  },
  {
    description: 'Looking for you next career opportunity? See all of our open positions',
    href: '#',
    name: 'Careers'
  },
  {
    description:
      'Get in touch with our dedicated support team or reach out on our community forums',
    href: '#',
    name: 'Support'
  },
  {
    description: 'Read our latest announcements and get perspectives from our team',
    href: '#',
    name: 'Blog'
  }
]

const menuContent = { align: 'center', sideOffset: 12 } as const

const productsUi = {
  content:
    'bg-surface-canvas dark:-outline-offset-1 dark:bg-surface-level-800 dark:outline-focus-inverse/10 dark:shadow-none max-w-md outline-1 outline-focus-level-900/5 overflow-hidden rounded-3xl shadow-lg w-screen ring-0'
} as const

const companyUi = {
  content:
    'bg-surface-canvas dark:-outline-offset-1 dark:bg-surface-level-800 dark:outline-focus-inverse/10 outline-1 outline-focus-level-900/5 p-4 rounded-3xl shadow-lg w-96 ring-0'
} as const

const mobileMenuOpen = ref(false)

const openMobileMenu = () => {
  mobileMenuOpen.value = true
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}
</script>
